import React, { useState } from 'react';
import { Button, message, Modal, Select } from 'antd';
import { CheckCircleOutlined, WarningFilled } from '@ant-design/icons';
import API from '../../../../../API';
import { BDO, getReadableBdo, OrderType } from 'types';
import { AsyncButton } from 'components';
import './bdo-type-field.scss';

interface Props {
	orderType: OrderType.classic | OrderType.loose;
	orderId: number;
	value: BDO;
	editable: boolean;
	cardConfirmedAt: string | null;
	onChange: (bdo: BDO) => Promise<unknown>;
}

export const BdoTypeField: React.FC<Props> = ({ orderType, orderId, value, editable, cardConfirmedAt, onChange }) => {
	const [selected, setSelected] = useState<BDO>();
	const [dialogOpen, setDialogOpen] = useState(false);
	const [saving, setSaving] = useState(false);

	// Saves through the page; the page reloads the order, which swaps the card panel and the statement form.
	const apply = async () => {
		if (!selected) return;
		setSaving(true);
		try {
			await onChange(selected);
		} finally {
			setSaving(false);
			setDialogOpen(false);
			setSelected(undefined);
		}
	};

	// A finished document of the current type needs a confirmation first; the statement loads fresh.
	const isFinished = async () =>
		value === BDO.BDO
			? cardConfirmedAt !== null
			: !!(await API.orders2.order(orderType, orderId).statement())?.signedAt;

	const handleChange = async () => {
		if (!selected || selected === value) return;
		try {
			if (await isFinished()) {
				setDialogOpen(true);
				return;
			}
		} catch (e) {
			message.error(`${e}`);
			return;
		}
		await apply();
	};

	const closeDialog = () => {
		setDialogOpen(false);
		setSelected(undefined);
	};

	if (!editable) {
		return <span>{getReadableBdo(value)}</span>;
	}

	return (
		<div className="flex bdo-type-field">
			<Select
				size="large"
				value={selected ?? value}
				onChange={(bdo: BDO) => setSelected(bdo)}
				options={[
					{ value: BDO.BDO, label: getReadableBdo(BDO.BDO) },
					{ value: BDO.producing, label: getReadableBdo(BDO.producing) },
				]}
			/>
			<AsyncButton icon={<CheckCircleOutlined />} onClick={handleChange} disabled={!selected || selected === value}>
				Zmień
			</AsyncButton>

			<Modal
				className="bdo-type-change-modal"
				open={dialogOpen}
				onCancel={closeDialog}
				closable={false}
				centered
				width={420}
				footer={null}
			>
				<WarningFilled style={{ color: '#FAAD14' }} />
				<h1>Czy na pewno chcesz zmienić typ BDO?</h1>
				<h3>
					{value === BDO.BDO
						? 'Karta BDO jest potwierdzona. Potwierdzenie zostanie w systemie, ale przestanie się liczyć dla tego zamówienia.'
						: 'Oświadczenie jest podpisane. Oświadczenie zostanie w systemie, ale przestanie się liczyć dla tego zamówienia.'}
				</h3>
				<div className="bdo-type-change-modal-actions">
					<Button size="large" block onClick={closeDialog}>
						Nie
					</Button>
					<Button type="primary" size="large" danger block loading={saving} onClick={apply}>
						Tak
					</Button>
				</div>
			</Modal>
		</div>
	);
};
